import {UI} from "../../stem-core/ui/UIBase";
import {styleRule, StyleSheet} from "../../stem-core/ui/Style";
import {MerchantUser} from "../../client/state/merchant/MerchantUserStore";
import {MerchantReportType} from "../../client/state/merchant/MerchantReportStore";
import {DashboardTableColumnManager, DashboardTablePage, MakeEmailEntry} from "../common/DashboardTablePage";
import {Money} from "../../stem-core/localization/Money.js";
import {InfoBlockGroup} from "../common/InfoBlock";
import {CENTER_COLUMN, DATE_COLUMN, LEFT_COLUMN, MONEY_COLUMN} from "../common/theme/TableStyle";
import {registerStyle, Theme} from "../../stem-core/ui/style/Theme";
import {Button} from "../../stem-core/ui/button/Button";
import {MerchantAddUserModal} from "../common/modals/MerchantAddUserModal";
import {MakeDeviceInfoLabel, RenderSessionDeviceInfo, UserDetailsModal} from "../common/UserDetails";
import {Annotation, GroupByField, AggregateDataViewManager} from "../common/AggregateDataViewManager";
import {capitalize} from "../../stem-core/base/Utils";
import {UserSearchInput} from "../ui/input/UserSearchInput.jsx";
import {formatPercentFromRatio} from "../../stem-core/base/Formatting.js";


class MerchantUserPageStyle extends StyleSheet {
    @styleRule
    filterSection = {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    }
}

@registerStyle(MerchantUserPageStyle)
export class MerchantUsersPage extends DashboardTablePage<MerchantUser> {
    declare userSearchInput: UserSearchInput;

    getDefaultOptions() {
        return {
            title: "Users",
            description: "All the users that accessed your website through Blink.",
            store: MerchantUser,
            endpoint: "/merchant/get_users/",
            reportType: MerchantReportType.USERS,
        }
    }

    getFilterSection() {
        return <div className={this.styleSheet.filterSection}>
            <UserSearchInput
                ref="userSearchInput"
                onChange={() => this.applyFilters()} />

            <Button onClick={() => {
                MerchantAddUserModal.show({
                    onSuccess: (merchantUser: MerchantUser) => this.handleUserAdded(merchantUser)
                });
            }}>Add user</Button>
        </div>
    }

    getFilters() {
        return {
            userFilter: this.userSearchInput.getValue(),
        };
    }

    handleUserAdded(merchantUser: MerchantUser) {
        UserDetailsModal.show({merchantUser})
        this.paginator.fetchFirstPage();
    }

    onMount() {
        super.onMount();
        // Refetch after a merge, the deleted user may be in the current page.
        this.attachListener(MerchantUser, "usersMerged", () => this.paginator.fetchFirstPage());
    }

    // From query_merchant_users_paginated with include_summary
    summaryContent(summaryLastResponse: {count: number; revenue: number; deviceDistribution: {mobile: number; desktop: number; tablet: number; unknown: number}}) {
        const {count, revenue, deviceDistribution} = summaryLastResponse;

        function formatDeviceCount(deviceCount: number) {
            return formatPercentFromRatio(deviceCount, count);
        }

        return [
            <InfoBlockGroup label="Overview" entries={[
                ["users", count],
                ["lifetime revenue", new Money(revenue, this.getCurrency())],
            ]}/>,
            (count > 0) && <InfoBlockGroup label="Breakdown by device" entries={[
                ["mobile", formatDeviceCount(deviceDistribution.mobile)],
                ["desktop", formatDeviceCount(deviceDistribution.desktop)],
                ["tablet", formatDeviceCount(deviceDistribution.tablet)],
                ["other", formatDeviceCount(deviceDistribution.unknown)],
            ]}/>
        ];
    }

    columnManager = new DashboardTableColumnManager<MerchantUser>("users-table-columns", [
        ["Email", merchantUser => MakeEmailEntry(merchantUser), LEFT_COLUMN],
        ["Name", merchantUser => merchantUser.getName(), LEFT_COLUMN],
        ["Lifetime Revenue", merchantUser => merchantUser.getLifetimeRevenue().toMainUnitString(), MONEY_COLUMN],
        ["Last Accessed", merchantUser => merchantUser.lastAccessDate, DATE_COLUMN],
        ["Last Device", merchantUser => {
            const lastDevice = merchantUser.getLastDevice();
            return RenderSessionDeviceInfo(lastDevice);
        }, CENTER_COLUMN],
        ["Last Location", merchantUser => {
            const location = merchantUser.getLastLocation();

            return location && <div>
                {location.city && <div>{location.city}</div>}
                <div>{[location.region, location.countryCode].filter(x => !!x).join(", ")}</div>
            </div>
        }]
    ]);
    groupsManager = new AggregateDataViewManager([
        new Annotation("Lifetime revenue", "lifetime_revenue", "sum", "lifetime_revenue", value => new Money(value, this.getCurrency())),
    ], [
        new GroupByField("Last access device type", "last_access_device_type", value => MakeDeviceInfoLabel(capitalize(value))),
    ])
}
